/**
 * AssignmentContext
 * 
 * Contexto centralizado para gerenciar atribuições de usuários.
 * Usa o AssignmentSyncService para sincronização.
 */

import React, { createContext, useContext, useCallback, useMemo, useEffect, useState, ReactNode } from 'react';
import { useOrganization } from '../OrganizationContext';
import { notify } from '../../utils/toast';
import { assignmentSyncService, EntityType } from '../../services/AssignmentSyncService';

interface AssignmentContextType {
    /**
     * Atribui um usuário a uma entidade e sincroniza com todas as entidades vinculadas
     */
    assignUser: (params: {
        entityType: EntityType;
        entityId: string;
        userId: string | null;
        phone?: string;
        silent?: boolean;
    }) => Promise<boolean>;

    /**
     * Contador de mudanças para forçar re-render em componentes que precisam
     */
    changeCounter: number;
}

const AssignmentContext = createContext<AssignmentContextType | undefined>(undefined);

export const AssignmentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentOrganization } = useOrganization();
    const [changeCounter, setChangeCounter] = useState(0);

    // Listener para eventos de mudança de atribuição
    useEffect(() => {
        const handleAssignmentChange = () => {
            setChangeCounter(prev => prev + 1);
        };

        window.addEventListener('assignment:changed', handleAssignmentChange);
        return () => window.removeEventListener('assignment:changed', handleAssignmentChange);
    }, []);

    /**
     * Atribui usuário a uma entidade
     */
    const assignUser = useCallback(async ({
        entityType,
        entityId,
        userId,
        phone,
        silent = false
    }: {
        entityType: EntityType;
        entityId: string;
        userId: string | null;
        phone?: string;
        silent?: boolean;
    }): Promise<boolean> => {
        if (!currentOrganization?.id) {
            return false;
        }

        try {
            const success = await assignmentSyncService.assign({
                entityType,
                entityId,
                userId,
                phone,
                organizationId: currentOrganization.id
            });



            if (success && !silent) {
                notify.success(userId ? 'Responsável atualizado' : 'Atribuição removida');
            } else if (!success) {
                notify.error('Erro ao atualizar responsável');
            }

            return success;
        } catch (error) {
            console.error('❌ [AssignmentContext] ERRO CRÍTICO:', error);
            notify.error('Erro ao atualizar responsável');
            return false;
        }
    }, [currentOrganization]);

    const value = useMemo(() => ({
        assignUser,
        changeCounter
    }), [assignUser, changeCounter]);

    return (
        <AssignmentContext.Provider value={value}>
            {children}
        </AssignmentContext.Provider>
    );
};

export const useAssignment = (): AssignmentContextType => {
    const context = useContext(AssignmentContext);
    if (!context) {
        throw new Error('useAssignment must be used within an AssignmentProvider');
    }
    return context;
};
